import { AfterViewInit, Component, ElementRef, inject, input, model, viewChild, HostListener, OnDestroy, computed, effect } from '@angular/core';
import { DisplayMedia } from 'src/app/models/image';
import { LoggingService } from 'src/app/services/logging.service';
import { IonIcon } from "@ionic/angular/standalone";
import { LocalizationModule } from 'src/app/modules/localization/localization.module';
import { ValidationOutputComponent } from "../form/validation-output/validation-output.component";
import { ComponentMemory } from 'src/app/models/memory';

@Component({
  selector: 'app-audio-player',
  templateUrl: './audio-player.component.html',
  styleUrls: ['./audio-player.component.scss'],
  standalone: true,
  imports: [IonIcon, LocalizationModule, ValidationOutputComponent]
})
export class AudioPlayerComponent implements AfterViewInit, OnDestroy, ComponentMemory {
  private loggingService = inject(LoggingService);

  media = input.required<DisplayMedia>();
  showTime = input(true);
  compact = input(false);

  audio = viewChild<ElementRef<HTMLAudioElement>>('audio');
  progressBar = viewChild<ElementRef<HTMLDivElement>>('progressBar');

  muted = model(false);
  paused = model(true);
  currentTime = model(0);
  duration = model(0);
  isDragging = model(false);
  loading = model(false);
  error = model<string | null>(null);
  hideBorder = input();

  progress = computed(() => this.duration() > 0 ? (this.currentTime() / this.duration()) * 100 : 0);

  constructor() {

    // Effect to handle media changes
    effect(() => {
      const media = this.media();
      const audioEl = this.audio()?.nativeElement;
      if (!audioEl) return;

      // Reset player state when media changes
      this.resetPlayer();

      // Only attempt to load if we have a src
      if (this.media().src) {
        this.loadAudio();
      }
    }, { allowSignalWrites: true });
  }

  init(): void {
    this.setupAudioListeners();
    if (this.media().src) {
      this.loadAudio();
    }
  }

  dispose(): void {
    this.cleanupAudioListeners();
    this.pause();
  }

  ngAfterViewInit() {
    this.init();
  }

  ngOnDestroy() {
    this.cleanupAudioListeners();
  }

  private resetPlayer() {
    this.paused.set(true);
    this.currentTime.set(0);
    this.duration.set(0);
    this.error.set(null);
  }

  private async loadAudio() {
    const audio = this.audio()?.nativeElement;
    if (!audio) return;

    try {
      this.loading.set(true);
      this.error.set(null);

      // Reset src and load new audio
      audio.src = this.media().src || '';
      audio.load();

      this.loading.set(false);
    } catch (err) {
      this.loading.set(false);
      this.error.set('Failed to load audio');
      this.loggingService.error('Audio load error', err);
    }
  }

  // Playback controls (unchanged but improved)
  play = async () => {
    if (this.error()) {
      await this.loadAudio();
      if (this.error()) return;
    }

    try {
      await this.audio()?.nativeElement.play();
      this.paused.set(false);
    } catch (error) {
      this.loggingService.error('Audio playback failed', error);
      this.error.set('Playback failed');
    }
  };

  pause = () => {
    this.audio()?.nativeElement.pause();
    this.paused.set(true);
  };

  toggleMute(event?: Event) {
    event?.stopPropagation();
    const audio = this.audio();
    if (!audio) return;
    audio.nativeElement.muted = !audio.nativeElement.muted;
    this.muted.set(audio.nativeElement.muted);
  }

  togglePlay(event?: Event) {
    event?.stopPropagation();
    const audio = this.audio();
    if (!audio) return;
    audio.nativeElement.paused ? this.play() : this.pause();
  }

  // Seeking functionality
  startDrag(event: MouseEvent | TouchEvent) {
    event.preventDefault();
    this.isDragging.set(true);
    this.handleSeek(event);
  }

  @HostListener('document:mousemove', ['$event'])
  @HostListener('document:touchmove', ['$event'])
  handleDrag(event: MouseEvent | TouchEvent) {
    if (this.isDragging()) {
      this.handleSeek(event);
    }
  }

  @HostListener('document:mouseup')
  @HostListener('document:touchend')
  endDrag() {
    this.isDragging.set(false);
  }

  handleSeek(event: MouseEvent | TouchEvent) {
    const audio = this.audio();
    const progressBar = this.progressBar()?.nativeElement;
    if (!audio || !progressBar || !this.duration()) return;

    const clientX = this.getClientX(event);
    const rect = progressBar.getBoundingClientRect();
    const pos = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);

    audio.nativeElement.currentTime = pos * this.duration();
    this.currentTime.set(audio.nativeElement.currentTime);
  }

  private getClientX(event: MouseEvent | TouchEvent): number {
    return 'touches' in event ? (event.touches[0] as Touch).clientX : event.clientX;
  }

  // Time formatting
  formatTime(seconds: number): string {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  // Audio event listeners
  private setupAudioListeners() {
    const audio = this.audio()?.nativeElement;
    if (!audio) return;

    audio.addEventListener('timeupdate', this.updateTime);
    audio.addEventListener('durationchange', this.updateDuration);
    audio.addEventListener('ended', this.handleEnded);
    audio.addEventListener('pause', this.pause);
    audio.addEventListener('play', this.play);
    audio.addEventListener('error', this.handleError);
  }

  private cleanupAudioListeners() {
    const audio = this.audio()?.nativeElement;
    if (!audio) return;

    audio.removeEventListener('timeupdate', this.updateTime);
    audio.removeEventListener('durationchange', this.updateDuration);
    audio.removeEventListener('ended', this.handleEnded);
    audio.removeEventListener('pause', this.pause);
    audio.removeEventListener('play', this.play);
    audio.removeEventListener('error', this.handleError);
  }

  private updateTime = () => {
    const audio = this.audio();
    if (!audio) return;
    this.currentTime.set(audio.nativeElement.currentTime);
  };

  private updateDuration = () => {
    const audio = this.audio();
    if (!audio) return;
    this.duration.set(audio.nativeElement.duration);
  };

  private handleEnded = () => {
    this.paused.set(true);
    this.currentTime.set(0);
  };

  private handleError = () => {
    const audio = this.audio();
    if (!audio) return;
    this.loggingService.error('Audio playback error', audio.nativeElement.error);
  };
}